import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import {
  useDeleteSeason,
  useGenerateShareToken,
  useRevokeShareToken,
  useSeason,
} from '../api/hooks'
import type { SeasonDetail } from '../api/types'
import { ScreenShell } from '../components/ScreenShell'
import { formatDateBare } from '../datetime'
import {
  BackButton,
  GameThumb,
  MeepleAvatar,
  Pill,
  ScorePad,
  ScorePadRow,
  TopBar,
} from '../primitives'
import { formatSeasonDateRange, formatStandingMetric, winnerCalcLabel } from '../seasons/format'

export const SeasonDetailScreen = () => {
  const { id } = useParams({ from: '/season/$id' })
  const navigate = useNavigate()
  const detail = useSeason(id)

  if (detail.isLoading) {
    return (
      <ScreenShell>
        <div style={{ padding: 16, color: 'var(--ink-3)' }}>Loading…</div>
      </ScreenShell>
    )
  }
  if (!detail.data) {
    return (
      <ScreenShell>
        <div style={{ padding: 16, color: 'var(--ink-2)' }}>Season not found.</div>
      </ScreenShell>
    )
  }

  const d = detail.data
  const { season, viewerIsAdmin } = d

  return (
    <ScreenShell>
      <TopBar
        left={
          <BackButton
            fallback={() => navigate({ to: '/group/$id', params: { id: season.groupId } })}
          />
        }
        title={season.name}
        sub={winnerCalcLabel(season.winnerCalcMethod)}
      />

      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div
          style={{
            padding: 14,
            background: 'var(--felt)',
            color: '#FFF8E8',
            border: '1px solid var(--felt-ink)',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div>
            <div className="kicker" style={{ color: 'rgba(255,248,232,.7)' }}>
              {formatSeasonDateRange(season.startAt, season.endAt)}
            </div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 18 }}>
              {d.standings.length} player{d.standings.length === 1 ? '' : 's'} · {d.plays.length}{' '}
              play
              {d.plays.length === 1 ? '' : 's'}
            </div>
          </div>
          <Pill
            color={d.status === 'active' ? 'gilt' : d.status === 'scheduled' ? 'plum' : 'slate'}
          >
            {d.status}
          </Pill>
        </div>

        <Standings detail={d} />

        <IncludedGames detail={d} />

        <PlaysCounted detail={d} />

        {viewerIsAdmin && (
          <Link
            to="/season/$id/edit"
            params={{ id: season.id }}
            className="btn-ghost"
            style={{ textAlign: 'center', textDecoration: 'none' }}
          >
            Edit season
          </Link>
        )}

        {viewerIsAdmin && <ShareControls detail={d} />}

        {viewerIsAdmin && <DeleteSeason detail={d} navigate={navigate} />}
      </div>
    </ScreenShell>
  )
}

const Standings = ({ detail }: { detail: SeasonDetail }) => {
  const { season, standings } = detail
  if (!standings.length) {
    return (
      <div>
        <Heading>Standings</Heading>
        <div className="felt-card" style={{ padding: 14, color: 'var(--ink-2)' }}>
          <div style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic', fontSize: 16 }}>
            No qualifying plays yet.
          </div>
          <div style={{ marginTop: 4, fontSize: 13 }}>
            Standings appear once group members record plays in the season window.
          </div>
        </div>
      </div>
    )
  }
  return (
    <div>
      <Heading>Standings</Heading>
      <ScorePad>
        {standings.map((s) => (
          <ScorePadRow key={s.userId} style={{ opacity: s.qualified ? 1 : 0.55 }}>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--ink-3)',
                minWidth: 22,
              }}
            >
              {String(s.rank).padStart(2, '0')}
            </span>
            <MeepleAvatar name={s.displayName} hue={s.avatarHue} shape={s.avatarShape} size={32} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{s.displayName}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                @{s.handle}
                {!s.qualified && ' · below threshold'}
              </div>
            </div>
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: 'var(--ink)',
                fontWeight: 600,
              }}
            >
              {formatStandingMetric(season.winnerCalcMethod, s)}
            </div>
          </ScorePadRow>
        ))}
      </ScorePad>
    </div>
  )
}

const IncludedGames = ({ detail }: { detail: SeasonDetail }) => {
  const games = detail.includedGames
  return (
    <div>
      <Heading>
        {detail.season.includedGames.length === 0
          ? 'Included games (all)'
          : `Included games · ${games.length}`}
      </Heading>
      {games.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>None yet.</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {games.map((g) => (
            <Pill key={g.id} color="slate">
              {g.name}
            </Pill>
          ))}
        </div>
      )}
    </div>
  )
}

const PlaysCounted = ({ detail }: { detail: SeasonDetail }) => {
  const plays = detail.plays.slice(0, 8)
  if (plays.length === 0) return null
  return (
    <div>
      <Heading>
        {detail.plays.length > plays.length
          ? `Recent plays · ${plays.length} of ${detail.plays.length}`
          : 'Plays counting'}
      </Heading>
      <ScorePad>
        {plays.map((p) => (
          <ScorePadRow key={p.playId}>
            <GameThumb label={p.game.name} imageUrl={p.game.imageUrl} size={28} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <Link
                to="/play/$id"
                params={{ id: p.playId }}
                style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}
              >
                {p.game.name}
              </Link>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                {formatDateBare(p.playedAt)}
              </div>
            </div>
            <span style={{ color: 'var(--ink-3)' }}>→</span>
          </ScorePadRow>
        ))}
      </ScorePad>
    </div>
  )
}

const ShareControls = ({ detail }: { detail: SeasonDetail }) => {
  const { season } = detail
  const generate = useGenerateShareToken(season.id)
  const revoke = useRevokeShareToken(season.id)
  const [copied, setCopied] = useState(false)

  const isActive = Boolean(season.shareToken && !season.shareRevokedAt)
  const url = isActive ? `${window.location.origin}/s/season/${season.shareToken}` : null

  const onGenerate = () => generate.mutate()
  const onRevoke = () => revoke.mutate()
  const onCopy = async () => {
    if (!url) return
    await navigator.clipboard.writeText(url)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div>
      <Heading>Public share link</Heading>
      <div
        style={{
          padding: 12,
          background: 'var(--paper-2)',
          border: '1px solid var(--line-soft)',
          borderRadius: 4,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {isActive ? (
          <>
            <code
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                color: 'var(--ink-2)',
                wordBreak: 'break-all',
              }}
            >
              {url}
            </code>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={onCopy} className="btn-stamp" style={{ flex: 1 }}>
                {copied ? 'Copied!' : 'Copy link'}
              </button>
              <button type="button" onClick={onRevoke} className="btn-ghost" style={{ flex: 1 }}>
                Revoke
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              {season.shareRevokedAt
                ? 'Share link was revoked. Generate a new one to re-share.'
                : 'Generate a public link to let anyone (no login) view standings.'}
            </div>
            <button type="button" onClick={onGenerate} className="btn-stamp">
              Generate link
            </button>
          </>
        )}
      </div>
    </div>
  )
}

const DeleteSeason = ({
  detail,
  navigate,
}: {
  detail: SeasonDetail
  navigate: ReturnType<typeof useNavigate>
}) => {
  const del = useDeleteSeason(detail.season.groupId)
  const [confirm, setConfirm] = useState(false)

  const onDelete = async () => {
    await del.mutateAsync(detail.season.id)
    navigate({ to: '/group/$id', params: { id: detail.season.groupId } })
  }

  if (!confirm) {
    return (
      <button type="button" onClick={() => setConfirm(true)} className="btn-ghost">
        Delete season
      </button>
    )
  }
  return (
    <div
      style={{
        padding: 12,
        background: 'var(--terra-bg)',
        border: '1px solid var(--terra)',
        borderRadius: 4,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontSize: 13, color: 'var(--terra-ink)' }}>
        Delete this season? Standings disappear; plays and ratings are unaffected.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => setConfirm(false)}
          className="btn-ghost"
          style={{ flex: 1 }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="btn-stamp"
          style={{ flex: 1, background: 'var(--terra)', borderColor: 'var(--terra)' }}
          disabled={del.isPending}
        >
          {del.isPending ? 'Deleting…' : 'Yes, delete'}
        </button>
      </div>
    </div>
  )
}

const Heading = ({ children }: { children: React.ReactNode }) => (
  <div className="kicker" style={{ marginBottom: 6 }}>
    {children}
  </div>
)
